import { useMemo, useState } from "react";
import { api } from "../api/client.js";
import { formatMinorAmount } from "../config/stripe.js";

const hasPoBox = (value) => /\b(?:p\.?\s*o\.?|post\s+office)\s*box\b/i.test(value);

function ShippingField({ label, name, value, onChange, error, ...inputProps }) {
  const errorId = `shipping-${name.replace(".", "-")}-error`;

  return (
    <label className="purchase-field">
      <span>{label}</span>
      <input
        name={name}
        value={value}
        onChange={onChange}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        {...inputProps}
      />
      {error && (
        <small id={errorId} className="form-field-error">
          {error}
        </small>
      )}
    </label>
  );
}

export default function MockUpsShippingStep({
  customer,
  billingAddress,
  fieldErrors,
  error,
  initializing,
  productAmount,
  currency,
  summaryLabel = "Products",
  onChange,
  onApplySuggestedAddress,
  onShippingSelected,
  onBack,
  onSubmit,
  items = [],
  previewOnly = false,
}) {
  const [validationError, setValidationError] = useState("");
  const [addressValidated, setAddressValidated] = useState(false);
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [selectedRateId, setSelectedRateId] = useState("");
  const [rates, setRates] = useState([]);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteNotice, setQuoteNotice] = useState("");

  const selectedRate = useMemo(
    () => rates.find((rate) => rate.id === selectedRateId),
    [rates, selectedRateId],
  );

  const suggestedAddress = useMemo(
    () => ({
      ...billingAddress,
      country: "US",
      line1: billingAddress.line1
        .replace(/\s+/g, " ")
        .replace(/\bct\b/i, "Ct")
        .trim(),
      city: billingAddress.city.trim(),
      state: billingAddress.state.trim().toUpperCase(),
      zip: billingAddress.zip.trim(),
    }),
    [billingAddress],
  );

  const previewTotal = productAmount + (selectedRate?.amount || 0);

  const handleFieldChange = (event) => {
    setValidationError("");
    setAddressValidated(false);
    setAddressConfirmed(false);
    setSelectedRateId("");
    setRates([]);
    setQuoteNotice("");
    onShippingSelected?.(null);
    onChange(event);
  };

  const validateAddress = () => {
    setValidationError("");

    if (hasPoBox(billingAddress.line1)) {
      setAddressValidated(false);
      setAddressConfirmed(false);
      setValidationError(
        "We cannot ship UPS orders to PO Boxes. Please enter a residential or commercial street address.",
      );
      return;
    }

    const requiredFields = [
      customer.fullName,
      customer.email,
      customer.phone,
      billingAddress.line1,
      billingAddress.city,
      billingAddress.state,
      billingAddress.zip,
    ];

    if (requiredFields.some((value) => !value.trim())) {
      setAddressValidated(false);
      setAddressConfirmed(false);
      setValidationError("Complete the required shipping details first.");
      return;
    }

    setAddressValidated(true);
    setAddressConfirmed(false);
  };

  const getCustomerNames = () => {
    const parts = customer.fullName.trim().split(/\s+/).filter(Boolean);
    return {
      firstName: parts[0] || "Customer",
      lastName: parts.slice(1).join(" ") || parts[0] || "Customer",
    };
  };

  const confirmSuggestedAddress = async () => {
    onApplySuggestedAddress(suggestedAddress);
    setAddressConfirmed(true);
    setQuoteLoading(true);
    setQuoteNotice("");
    setSelectedRateId("");
    setRates([]);
    onShippingSelected?.(null);

    try {
      const response = await api.shipping.quote({
        customer: {
          ...getCustomerNames(),
          email: customer.email.trim(),
          phone: customer.phone.trim(),
        },
        address: {
          ...suggestedAddress,
          residential: true,
        },
        items,
      });
      const nextRates = (response.data?.rates || []).map((rate) => ({
        id: `${rate.serviceCode}-${rate.amount}-${rate.estimatedDelivery || "none"}`,
        serviceCode: rate.serviceCode,
        name: rate.serviceName,
        amount: rate.amount,
        currency: rate.currency,
        estimatedDelivery: rate.estimatedDelivery || "",
      }));

      if (nextRates.length > 0) {
        setRates(nextRates);
        setSelectedRateId(nextRates[0].id);
        onShippingSelected?.({
          serviceCode: nextRates[0].serviceCode,
          serviceName: nextRates[0].name,
          amount: nextRates[0].amount,
          currency: nextRates[0].currency,
          estimatedDelivery: nextRates[0].estimatedDelivery,
        });
        return;
      }

      setQuoteNotice("UPS did not return delivery rates for this address.");
      onShippingSelected?.(null);
    } catch (requestError) {
      setQuoteNotice(
        requestError.message ||
          "UPS delivery rates are not available right now. Please try again.",
      );
      onShippingSelected?.(null);
    } finally {
      setQuoteLoading(false);
    }
  };

  const handleSubmit = (event) => {
    if (previewOnly) {
      event.preventDefault();
      return;
    }
    if (!selectedRate?.serviceCode) {
      event.preventDefault();
      setValidationError("Choose an available UPS delivery option first.");
      return;
    }
    onSubmit(event);
  };

  return (
    <form className="purchase-details-form" onSubmit={handleSubmit} noValidate>
      <div className="purchase-field-grid">
        <ShippingField
          label="Full name *"
          name="customer.fullName"
          value={customer.fullName}
          onChange={handleFieldChange}
          error={fieldErrors["customer.fullName"]}
          autoComplete="name"
        />
        <ShippingField
          label="Email *"
          name="customer.email"
          value={customer.email}
          onChange={handleFieldChange}
          error={fieldErrors["customer.email"]}
          type="email"
          autoComplete="email"
        />
        <ShippingField
          label="Phone *"
          name="customer.phone"
          value={customer.phone}
          onChange={handleFieldChange}
          error={fieldErrors["customer.phone"]}
          type="tel"
          autoComplete="tel"
        />
        <ShippingField
          label="Country *"
          name="billingAddress.country"
          value="US"
          onChange={handleFieldChange}
          error={fieldErrors["billingAddress.country"]}
          readOnly
          autoComplete="country"
        />
        <ShippingField
          label="Street address *"
          name="billingAddress.line1"
          value={billingAddress.line1}
          onChange={handleFieldChange}
          error={fieldErrors["billingAddress.line1"]}
          autoComplete="address-line1"
        />
        <ShippingField
          label="Apt / Suite / Unit"
          name="billingAddress.line2"
          value={billingAddress.line2}
          onChange={handleFieldChange}
          error={fieldErrors["billingAddress.line2"]}
          autoComplete="address-line2"
        />
        <ShippingField
          label="City *"
          name="billingAddress.city"
          value={billingAddress.city}
          onChange={handleFieldChange}
          error={fieldErrors["billingAddress.city"]}
          autoComplete="address-level2"
        />
        <ShippingField
          label="State *"
          name="billingAddress.state"
          value={billingAddress.state}
          onChange={handleFieldChange}
          error={fieldErrors["billingAddress.state"]}
          autoComplete="address-level1"
        />
        <ShippingField
          label="ZIP code *"
          name="billingAddress.zip"
          value={billingAddress.zip}
          onChange={handleFieldChange}
          error={fieldErrors["billingAddress.zip"]}
          autoComplete="postal-code"
        />
      </div>

      <div className="ups-preview-actions">
        <button
          type="button"
          className="purchase-secondary"
          onClick={validateAddress}
        >
          Validate address
        </button>
      </div>

      {validationError && (
        <p className="purchase-error" role="alert">
          {validationError}
        </p>
      )}

      {addressValidated && !addressConfirmed && (
        <section className="ups-address-confirmation" aria-live="polite">
          <div>
            <p>Suggested address</p>
            <strong>{suggestedAddress.line1}</strong>
            <span>
              {[suggestedAddress.city, suggestedAddress.state]
                .filter(Boolean)
                .join(", ")}{" "}
              {suggestedAddress.zip}
            </span>
          </div>
          <button
            type="button"
            className="purchase-primary"
            onClick={confirmSuggestedAddress}
          >
            Use suggested address
          </button>
        </section>
      )}

      {addressConfirmed && (
        <section className="ups-rate-panel" aria-labelledby="ups-rates-title">
          <div className="ups-rate-heading">
            <div>
              <p>UPS delivery options</p>
              <h3 id="ups-rates-title">Choose delivery</h3>
            </div>
          </div>

          <div className="ups-rate-list">
            {quoteLoading && (
              <p className="purchase-loading" role="status">
                Loading UPS rates...
              </p>
            )}
            {quoteNotice && <p className="ups-preview-note">{quoteNotice}</p>}
            {!quoteLoading && rates.length === 0 && !quoteNotice && (
              <p className="ups-preview-note">
                Confirm the suggested address to load live UPS rates.
              </p>
            )}
            {rates.map((rate) => (
              <label
                className={`ups-rate-option${
                  selectedRateId === rate.id ? " is-selected" : ""
                }`}
                key={rate.id}
              >
                <input
                  type="radio"
                  name="upsRate"
                  value={rate.id}
                  checked={selectedRateId === rate.id}
                  onChange={() => {
                    setSelectedRateId(rate.id);
                    onShippingSelected?.({
                      serviceCode: rate.serviceCode,
                      serviceName: rate.name,
                      amount: rate.amount,
                      currency: rate.currency,
                      estimatedDelivery: rate.estimatedDelivery,
                    });
                  }}
                />
                <span>
                  <strong>{rate.name}</strong>
                  <small>
                    {rate.estimatedDelivery || "UPS estimate unavailable"}
                  </small>
                </span>
                <b>{formatMinorAmount(rate.amount, rate.currency)}</b>
              </label>
            ))}
          </div>

          <div className="ups-order-summary">
            <div>
              <span>{summaryLabel}</span>
              <strong>{formatMinorAmount(productAmount, currency)}</strong>
            </div>
            <div>
              <span>Shipping</span>
              <strong>
                {selectedRate
                  ? formatMinorAmount(selectedRate.amount, selectedRate.currency)
                  : "Unavailable"}
              </strong>
            </div>
            <div>
              <span>Total</span>
              <strong>{formatMinorAmount(previewTotal, currency)}</strong>
            </div>
            <small>Tax included</small>
          </div>
        </section>
      )}

      {error && (
        <p className="purchase-error" role="alert">
          {error}
        </p>
      )}

      <div className="purchase-actions">
        <button
          type="button"
          className="purchase-secondary"
          onClick={onBack}
          disabled={initializing}
        >
          Back
        </button>
        <button
          type="submit"
          className="purchase-primary"
          disabled={
            initializing ||
            !addressConfirmed ||
            !selectedRate?.serviceCode ||
            previewOnly
          }
        >
          {previewOnly
            ? "Payment pending UPS setup"
            : initializing
              ? "Preparing secure payment..."
              : "Continue to payment"}
        </button>
      </div>
    </form>
  );
}
